import React, {
    createContext,
    useReducer,
    useState,
    useMemo,
    useRef,
    useEffect,
    useContext,
} from "react";
import { useIngredientsStock, useProductStock } from "../../Api/ingredientsApi";
import PropTypes from "prop-types";
import Portal from "~/assets/js/components/Portal";
import { prepareIngredients, formatPrice } from "../../Format";
import AddToCartSuccess from "./components/Notifications/AddToCartSuccess";
import {
    AddToBasketErrorAlert,
    NoDressingSelectedAlert,
    ProductDetailsErrorAlert,
    ProductOutOfStockAlert,
} from "./components/Notifications/Alerts";
import Decimal from "decimal.js";
import { DeliveryContext } from "../../models/DeliveryContext";
import useSmoothScroll from "../../Helpers/hooks/useSmoothScroll";
import { calculateFranchisePrice } from "../../Helpers/util";
import { useCartContext } from "~/assets/js/global/cartContext";
import { useUser } from "~/assets/js/Api/userApi";
import GreenClubSession from "~/assets/js/global/greenClub";
import FullPageLoader from "~/assets/js/components/Loaders/FullPageLoader";
// import GreenClubOnboardingPopup from "~/assets/js/components/GreenClub/GreenClubOnboardingPopup";
import GreenClubCapacityPopUp from "~/assets/js/components/GreenClub/GreenClubCapacityPopUp";
import { fetchStoreData, getProduct } from "../../Api/storeApi";
import useCustomEvent from "../../Helpers/hooks/useCustomEvent";
import useProductCallbackListener from "../../Helpers/hooks/useProductCallbackListener";

const MAX_ADDITIONAL_INGREDIENTS = 3;

const ERROR = {
    // The customer wants to remove the base ingredient (the base is defined in wordpress).
    REMOVE_BASE_INGREDIENT: "removeBase",
    // The customer wants to remove more than one basic ingredient.
    REMOVE_MULTIPLE_BASIC: "removeMultipleBasic",
    // The customer wants to choose more than three extra ingredients.
    MAX_EXTRA_INGREDIENTS: "maxExtra",
    // Error when adding to cart
    ADD_TO_BASKET_ERROR: "addToBasketError",
    // Error when user wants to add out of stock product to cart
    OUT_OF_STOCK: "outOfStock",
    // Error when users submits product without dressigns
    NO_DRESSINGS_SELECTED: "noDressingsError",
};

export const ACTIONS = {
    ADD_DRESSING: "ADD_DRESSING",
    ADD_EXTRA: "ADD_EXTRA",
    UPDATE: "UPDATE",
    REMOVE: "REMOVE",
    UPDATE_QUANTITY: "UPDATE_QUANTITY",
    CLEAR: "CLEAR",
};

export const ITEM_TYPE = {
    BASIC: "basic",
    EXTRA: "extras",
    DRESSINGS: "dressings",
};

export const UPDATE_TYPE = {
    INCREMENT: "increment",
    DECREMENT: "decrement",
};

export const LOCATION_CALLBACK_ACTIONS = {
    SUBMIT: "submitItemToCart",
};

export const ProductsDetailsContext = createContext({});

const ProductsDetailsReducer = (state, action) => {
    const { type, payload } = action;
    switch (type) {
        case ACTIONS.ADD_DRESSING:
            return { ...state, dressings: [...state.dressings, payload] };
        case ACTIONS.ADD_EXTRA:
            return { ...state, extras: [...state.extras, payload] };
        case ACTIONS.UPDATE:
            // if item type not in state
            if (!Object.keys(state).includes(payload.itemType)) {
                return state;
            }

            return {
                ...state,
                [payload.itemType]: state[payload.itemType].map((item) => {
                    if (
                        item.product_id === payload.product_id &&
                        payload.update === UPDATE_TYPE.INCREMENT
                    ) {
                        return { ...item, quantity: item.quantity + 1 };
                    }
                    if (
                        item.product_id === payload.product_id &&
                        payload.update === UPDATE_TYPE.DECREMENT
                    ) {
                        return { ...item, quantity: item.quantity - 1 };
                    }
                    return { ...item };
                }),
            };
        case ACTIONS.REMOVE:
            if (payload.itemType === ITEM_TYPE.BASIC) {
                return {
                    ...state,
                    basic: state.basic.map((item) => {
                        if (item.product_id === payload.product_id) {
                            return { ...item, quantity: 0 };
                        }
                        return { ...item };
                    }),
                };
            }

            return {
                ...state,
                [payload.itemType]: state[payload.itemType].filter(
                    (item) => item.product_id !== payload.product_id
                ),
            };
        case ACTIONS.UPDATE_QUANTITY:
            return { ...state, quantity: parseInt(payload) };
        case ACTIONS.CLEAR:
            return { ...state, extras: [], dressings: [], quantity: 1 };
        default:
            throw new Error(
                `Action ${type} with ${payload} did not match any statement`
            );
    }
};

const init = (initialState) => {
    return {
        dressings: initialState.dressings,
        basic: pottsalatProduct.ingredients,
        extras: [],
        productId: pottsalatProduct.product_id,
        productCategory: pottsalatProduct.categories[0],
        productName: pottsalatProduct.title,
        quantity: initialState.quantity,
    };
};

const initialState = {
    productId: 0,
    productCategory: 0,
    productName: "",
    quantity: 1,
    dressings: [],
    basic: [],
    extras: [],
};

const {
    bowlCategory: BOWL_ID,
    saladCategory: SALADS_ID,
    soupCategory: SOUP_ID,
} = reactConfig;

const categoryData = {
    [BOWL_ID]: {
        title: "bowl",
        url: "meine-bowl",
    },
    [SALADS_ID]: {
        title: "salat",
        url: "mein-salat",
    },
    [SOUP_ID]: {
        title: "suppe",
        url: "meine-ramen",
    },
};

const ProductsProvider = ({ children }) => {
    const [state, dispatch] = useReducer(
        ProductsDetailsReducer,
        initialState,
        init
    );

    const [isLoading, setLoading] = useState(false);
    const { actions, cartError, cartItems, numberOfDiscountedProducts } =
        useCartContext();
    const { user, isLoading: userLoading } = useUser();
    const isGreenClubMember = !!user && !!user.membership_count;
    const [successPopup, setSuccessPopup] = useState(false);
    const [maxGreenClubCapacityPopup, setMaxGreenClubCapacityPopup] =
        useState(false);
    const [error, setError] = useState(null);
    const [selectGreenClubPrice, setSelectGreenClubPrice] = useState(false); // const [isGreenClubSelected, setGreenClubSelected] = useState(false); -> BETTER NAME FOR READABILITY
    const [isModified, setIsModified] = useState(false);
    const isInitialMount = useRef(true);
    const { storeId } = useContext(DeliveryContext);
    const { triggerEvent } = useCustomEvent("trigger-address-popup");
    const isDiscoverMode = !storeId;

    const {
        greenClubEnabled: storeHasGreenClubEnabled,
        franchiseId: franchiseId,
    } = fetchStoreData(storeId);

    const { product, productLoading } = getProduct(
        state.productId,
        franchiseId,
        storeId
    );

    const {
        inStock,
        isLoading: productStockLoading,
        mutateProductStock,
    } = useProductStock(storeId);

    const isBowl = pottsalatProduct.categories.includes(BOWL_ID);
    const isSalad = pottsalatProduct.categories.includes(SALADS_ID);
    const isSoup = pottsalatProduct.categories.includes(SOUP_ID);
    const isMainDish = isBowl || isSalad || isSoup;
    const isSauceProduct = isSoup;

    const {
        dressings,
        sauces,
        isLoading: stocksLoading,
        mutateIngredientsStock,
    } = useIngredientsStock(storeId);

    const preselectRecommendedDressing = (availableDressings) => {
        const noRecommendedDressing =
            !pottsalatProduct.details.recommended_dressings;

        if (noRecommendedDressing) {
            return;
        }

        const recommendedDressingId =
            pottsalatProduct.details.recommended_dressings;

        const recommendedDressing = availableDressings.find(
            (dressing) => dressing.id === recommendedDressingId
        );

        if (recommendedDressing) {
            const data = {
                product_id: recommendedDressing.product_id,
                quantity: 1,
                local_prices: recommendedDressing.local_prices,
                price: recommendedDressing.price,
                name: recommendedDressing.name,
                image: recommendedDressing.images.normal,
                details: recommendedDressing.details,
            };
            dispatch({
                type: ACTIONS.ADD_DRESSING,
                payload: data,
            });
        }
    };

    const adjustQuantity = (isProductInStock) => {
        if (!isProductInStock)
            dispatch({ type: ACTIONS.UPDATE_QUANTITY, payload: 0 });
    };

    const clearConfiguration = () => dispatch({ type: ACTIONS.CLEAR });

    useEffect(() => {
        setMaxGreenClubCapacityPopup(
            !GreenClubSession.get()?.hideGreenClubMaxCapacityPopup &&
                isGreenClubMember &&
                numberOfDiscountedProducts >= user?.membership_count
        );
    }, [user, cartItems]);

    useEffect(() => {
        if (storeId && !isInitialMount.current) handleDeliveryAreaUpdate();
    }, [storeId]);

    useProductCallbackListener(async ({ detail }) => {
        const { callbackAction } = detail;
        switch (callbackAction) {
            case LOCATION_CALLBACK_ACTIONS.SUBMIT:
                const stock = await mutateProductStock();
                const discoverMode = false;
                await submitProductToCart(true, stock, discoverMode);
                break;
            default:
                await mutateProductStock();
                break;
        }
    });

    const handleDeliveryAreaUpdate = async () => {
        const stock = await mutateProductStock();
        const inStockIngredients = await mutateIngredientsStock();
        clearConfiguration();
        adjustQuantity(stock);
        preselectRecommendedDressing([
            ...inStockIngredients.dressings,
            ...inStockIngredients.sauces,
        ]);
    };

    const triggerAddressPopup = (reason = "unknown") => {
        triggerEvent({
            forceSelection: true,
            callbackAction: reason,
        });
    };

    /**
     * Submit the preconfigured product with addons to cart
     * @returns redirect to cart if succesfull, error alert otherwise
     */
    const submitProductToCart = async (
        dressingCheck = true,
        stock = inStock,
        discoverMode = isDiscoverMode,
        menuItems = undefined
    ) => {
        if (discoverMode) {
            triggerAddressPopup(LOCATION_CALLBACK_ACTIONS.SUBMIT);
            return;
        }

        if (!stock) {
            return setError(ERROR.OUT_OF_STOCK);
        }

        const { dressings, basic, extras } = state;

        if (
            dressings.length <= 0 &&
            (isSoup || isSalad || isBowl) &&
            pottsalatProduct.details.dressing_included !== false &&
            dressingCheck
        ) {
            return setError(ERROR.NO_DRESSINGS_SELECTED);
        }

        const { ingredients, removedIngredient } = prepareIngredients({
            dressings,
            basic,
            extras,
        });

        setLoading(true);

        const itemsToDiscount =
            state.quantity > discountedProductsAvailable
                ? discountedProductsAvailable
                : state.quantity;

        await actions.addConfigurableProductToCart(
            parseInt(state.productId),
            state.quantity,
            itemsToDiscount,
            ingredients,
            removedIngredient
                ? [{ product_id: removedIngredient, quantity: 1 }]
                : undefined,
            menuItems
        );

        setLoading(false);

        if (!cartError) {
            setSuccessPopup(true);
            return;
        }

        return setError(ERROR.ADD_TO_BASKET_ERROR);
    };

    const alertFromError = () => {
        const onClose = () => setError(null);
        const { categories } = pottsalatProduct;
        const { iconPath } = reactConfig;
        const category = categoryData[categories[0]]?.title || "other";
        let image = `${iconPath}/alert-triangle.svg`;
        const onRedirect = () => {
            const { categories } = pottsalatProduct;
            const redirect = `/${categoryData[categories[0]]?.url || "shop"}/`;
            location.href = redirect;
            return;
        };
        switch (error) {
            case ERROR.REMOVE_BASE_INGREDIENT:
            case ERROR.MAX_EXTRA_INGREDIENTS:
            case ERROR.REMOVE_MULTIPLE_BASIC:
                return (
                    <ProductDetailsErrorAlert
                        onClose={onClose}
                        onRedirect={onRedirect}
                        category={category}
                        image={image}
                        error={error}
                    />
                );
            case ERROR.ADD_TO_BASKET_ERROR:
                return (
                    <AddToBasketErrorAlert onClose={onClose} image={image} />
                );
            case ERROR.OUT_OF_STOCK:
                image = `${iconPath}/Cross.svg`;
                return (
                    <ProductOutOfStockAlert onClose={onClose} image={image} />
                );
            case ERROR.NO_DRESSINGS_SELECTED:
                image = `${iconPath}/Dressing.svg`;
                const handleNoDressingWanted = () => {
                    onClose();
                    submitProductToCart(false);
                };

                const handleWantsDressings = () => {
                    onClose();
                    useSmoothScroll("dressings", 0);
                };

                return (
                    <NoDressingSelectedAlert
                        onClose={handleNoDressingWanted}
                        image={image}
                        onRedirect={handleWantsDressings}
                        error={error}
                    />
                );
        }
    };

    const calculatePrice = (initialPriceBase, storeId = null) => {
        if (isDiscoverMode) {
            const defaultValue = new Decimal(
                parseFloat(pottsalatProduct.priceGross)
            );
            return formatPrice(defaultValue.toFixed(2));
        }

        let baseProductPrice = initialPriceBase || 0;

        // Initial prices
        let value = new Decimal(baseProductPrice);
        let extrasValue = new Decimal(0);
        let basicsValue = new Decimal(0);
        let dressingsValue = new Decimal(0);

        // How much was removed from the initial base package
        const removedFromPackage = new Decimal(
            state.basic.reduce(
                (total, basic) =>
                    basic.quantity === 0
                        ? total + calculateFranchisePrice(storeId, basic)
                        : total,
                0
            )
        );

        // Added additional basic ingredients
        basicsValue = new Decimal(
            state.basic.reduce((total, basic) => {
                const basicPrice = calculateFranchisePrice(storeId, basic);

                if (basic.quantity > 1) {
                    return total + basicPrice * (basic.quantity - 1); // calculate if more than one
                }

                return total;
            }, 0)
        );

        // Add calculated extras price on top of base price
        if (state.extras.length > 0) {
            extrasValue = new Decimal(
                state.extras.reduce((total, extra) => {
                    const extraPrice = calculateFranchisePrice(storeId, extra);
                    return total + extraPrice * extra.quantity;
                }, 0)
            );
        }

        // Add calculated dressings on top of base price
        if (state.dressings.length > 0) {
            dressingsValue = new Decimal(
                state.dressings.reduce((total, dressing, idx) => {
                    const dressingPrice = calculateFranchisePrice(
                        storeId,
                        dressing
                    );
                    const isFirstDressing = !idx;

                    // first dressing is free
                    if (isFirstDressing && dressing.quantity <= 1) {
                        return total;
                    }

                    if (isFirstDressing && dressing.quantity > 1) {
                        return total + dressingPrice * (dressing.quantity - 1);
                    }

                    return total + dressingPrice * dressing.quantity;
                }, 0)
            );
            value = value.add(dressingsValue);
        }

        const ingredientsAdded = basicsValue.add(extrasValue);
        const [somethingWasRemoved, somethingExtraWasAdded] = [
            removedFromPackage.greaterThan(0),
            ingredientsAdded.greaterThan(0),
        ];
        setIsModified(somethingWasRemoved && somethingExtraWasAdded);

        // Calculate amount modified and apply it to base price
        const modification = ingredientsAdded.sub(removedFromPackage);
        if (modification.greaterThan(0)) {
            value = value.add(modification);
        }

        const productQuantity =
            !isNaN(state.quantity) && state.quantity > 0 ? state.quantity : 1;
        value = value.times(productQuantity);
        return formatPrice(value.toFixed(2));
    };

    const fullPrice = useMemo(
        () => calculatePrice(product?.value_gross, storeId),
        [
            state.quantity,
            state.dressings,
            state.extras,
            state.basic,
            storeId,
            product,
        ]
    );

    const greenClubMembershipPrice = useMemo(
        () => calculatePrice(product?.greenclub_value_gross, storeId),
        [
            state.quantity,
            state.dressings,
            state.extras,
            state.basic,
            storeId,
            product,
        ]
    );

    /**
     * Wrapper around dispatch function
     * to ensure products follows the bussiness rules
     * at the end recalculate new price
     * defines which dispatch action to use if all check succesfull
     * @param {string} type
     *
     * payload to complete the action eg. id or entire ingredient obj
     * @param payload
     */
    const updateProductState = ({ type, payload }) => {
        if (isDiscoverMode) {
            triggerAddressPopup();
            return;
        }

        const updateOutOfStockProduct =
            type === ACTIONS.UPDATE_QUANTITY && !inStock;

        if (updateOutOfStockProduct) {
            return setError(ERROR.OUT_OF_STOCK);
        }

        // Removing basic ingredients check
        const removingBasic =
            type === ACTIONS.REMOVE && payload.itemType === ITEM_TYPE.BASIC;
        if (removingBasic) {
            const ingredient = state.basic.find(
                (ingr) => ingr.product_id === payload.product_id
            );
            const isBasis = ingredient.tags.some((tag) =>
                tag.includes("basis")
            );
            if (isBasis) return setError(ERROR.REMOVE_BASE_INGREDIENT);

            const wasAlreadyRemoved = state.basic.some(
                (ingr) => ingr.quantity === 0
            );
            if (wasAlreadyRemoved) return setError(ERROR.REMOVE_MULTIPLE_BASIC);
        }

        const isAddExtra =
            type === ACTIONS.ADD_EXTRA ||
            (type === ACTIONS.UPDATE &&
                payload.itemType === ITEM_TYPE.EXTRA &&
                payload.update === UPDATE_TYPE.INCREMENT);
        const isAddBasic =
            type === ACTIONS.UPDATE &&
            payload.itemType === ITEM_TYPE.BASIC &&
            payload.update === UPDATE_TYPE.INCREMENT;

        // adding extra ingredients check
        if (isAddExtra || isAddBasic) {
            const countAddedBasics = state.basic.reduce((total, basic) => {
                const basicQuantity =
                    basic.quantity - 1 >= 0 ? basic.quantity - 1 : 0;
                return total + basicQuantity;
            }, 0);

            const countAddedExtras = state.extras.reduce(
                (total, extra) => total + extra.quantity,
                0
            );

            const sum = countAddedBasics + countAddedExtras;

            const removedBasicIngredient = state.basic.find(
                (ingr) => ingr.quantity === 0
            );

            // Check if you add removed ingredient after you max out the additional
            const reAddingRemovedIngredient =
                removedBasicIngredient !== undefined &&
                removedBasicIngredient.product_id === payload.product_id;

            if (!reAddingRemovedIngredient && sum >= MAX_ADDITIONAL_INGREDIENTS)
                return setError(ERROR.MAX_EXTRA_INGREDIENTS);
        }

        return dispatch({ type, payload });
    };

    // Preselect the recommended dressing and adjust quantity
    if (!stocksLoading && !productStockLoading && isInitialMount.current) {
        preselectRecommendedDressing([...dressings, ...sauces]);
        adjustQuantity(inStock);
        isInitialMount.current = false;
    }

    // discover mode on mount
    if (isDiscoverMode && isInitialMount.current) {
        preselectRecommendedDressing(pottsalatIngredients?.products || []);
        isInitialMount.current = false;
    }

    // const showGreenClubOnbarding =
    //     storeId &&
    //     !isGreenClubMember &&
    //     isMainDish &&
    //     !GreenClubSession.get()?.hideOnboardingPopup;

    const discountedProductsAvailable =
        user?.membership_count - numberOfDiscountedProducts;

    if (userLoading || productLoading) {
        return <FullPageLoader />;
    }

    return (
        <ProductsDetailsContext.Provider
            value={{
                state,
                updateProductState,
                submit: submitProductToCart,
                isSubmitting: isLoading,
                fullPrice,
                greenClubMembershipPrice,
                isGreenClubMember,
                isMainDish,
                isBowl,
                isSalad,
                isSoup,
                isSauceProduct,
                inStock,
                isModified,
                isDiscoverMode,
                discountedProductsAvailable,
                storeId,
                selectGreenClubPrice,
                setSelectGreenClubPrice,
                triggerAddressPopup,
            }}>
            {/*
            TEMPORARY DISABLED

            {storeHasGreenClubEnabled && showGreenClubOnbarding && (
                <GreenClubOnboardingPopup
                    initialOpen={showGreenClubOnbarding}
                />
            )} */}

            {storeHasGreenClubEnabled && (
                <GreenClubCapacityPopUp
                    open={maxGreenClubCapacityPopup}
                    handleClose={() => {
                        setMaxGreenClubCapacityPopup(false);
                        const session = GreenClubSession.get();
                        GreenClubSession.update({
                            ...session,
                            hideGreenClubMaxCapacityPopup: true,
                        });
                    }}
                />
            )}
            <AddToCartSuccess
                open={successPopup}
                productName={pottsalatProduct.title}
                productImage={
                    pottsalatProduct.images
                        ? pottsalatProduct.images[0].src
                        : ""
                }
                onContinueBrowsing={() => {
                    setSuccessPopup(false);
                    window.location.href = productCategoryLink;
                }}
                onClose={() => setSuccessPopup(false)}
                onRedirect={() => (location.href = "/warenkorb/")}
                showNonDiscountedProducts={
                    isGreenClubMember &&
                    user?.membership_count < state.quantity &&
                    state.quantity > discountedProductsAvailable &&
                    discountedProductsAvailable > 0
                }
                discountedProducts={discountedProductsAvailable}
                nonDiscountedProducts={
                    state.quantity - discountedProductsAvailable
                }
            />
            <Portal show={!!error} onHide={() => setError(null)}>
                {alertFromError()}
            </Portal>
            {children}
        </ProductsDetailsContext.Provider>
    );
};

ProductsProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};

export default ProductsProvider;
